/**
 * <product-frequently-together>
 *
 * Renders the "Frequently bought together" (FBT) upsell on the PDP.
 * Migrated out of the Vue `product` component (assets/main.js): fetches the
 * recommendations section, injects it, and wires up the checkbox / variant /
 * CTA interactions. The final "add selected to cart" call is delegated back to
 * the global cart controller (`window.enrouteCart?.addToCart?.()`) so cart state
 * and the cart popup keep a single source of truth.
 *
 * Markup: an empty element carrying the main product id plus the server-resolved
 * first-paint recommendation target (the selected/first variant's related
 * product, else the main product), e.g.
 *   <product-frequently-together class="frequently-recommendations desktop-only"
 *                                product-id="123456"
 *                                recommend-product-id="789012"></product-frequently-together>
 * The recommendations body itself is fetched client-side from the Shopify
 * recommendations API (sections/product-frequently-together.liquid).
 */
if (!customElements.get('product-frequently-together')) {
  customElements.define(
    'product-frequently-together',
    class ProductFrequentlyTogether extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        // Suppress the standalone mobile copy only when the new PDP's inline
        // recommendations are also configured to appear on mobile.
        const inlineRecommendations = document.querySelector('section.product-details-new-2610 product-frequently-together');
        if (this.classList.contains('mobile-only') && inlineRecommendations && !inlineRecommendations.classList.contains('pd2610-desktop-only')) {
          const section = this.closest('.shopify-section');
          if (section) section.hidden = true;
          return;
        }
        this.productId =
          this.getAttribute('product-id') ||
          (typeof productID !== 'undefined' ? productID : null);

        if (!this.productId) return;

        if (this.querySelector('.pd2610-look-skeleton')) {
          this.loadingMarkup = this.innerHTML;
        }
        document.addEventListener('click', (event) => {
          if (!this.contains(event.target) || !event.target.closest('.pd2610-look-select, .pd2610-look-popup')) {
            this.closeLookPickers();
          }
        }, { signal: this.abortController.signal });
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          const popup = this.querySelector('.pd2610-look-popup:not([hidden])');
          if (!popup) return;
          this.closeLookPickers();
          popup.anchorElement?.focus();
        }, { signal: this.abortController.signal });
        this.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeLookPickers();
        }, { signal: this.abortController.signal });

        // The product this section is currently recommending for. Seeded from the
        // server-resolved first-paint target (the selected/first variant's related
        // product, else the main product); update(variant) may swap it later.
        this.recommendedForId = this.getAttribute('recommend-product-id') || this.productId;

        window.addEventListener(
          'enroute:variant-changed',
          (event) => {
            const variant = event?.detail?.variant;
            if (variant) this.update(variant);
          },
          { signal: this.abortController.signal }
        );

        // Non-critical below-the-fold content: defer until the browser is idle.
        // Load for the current target (related product when firstVariant maps to
        // one), not blindly the main product, so it agrees with the initial
        // enroute:variant-changed broadcast and avoids a main->related reload race.
        const start = () => this.loadRecommendations(this.recommendedForId);
        if ('requestIdleCallback' in window) {
          requestIdleCallback(start, { timeout: 1000 });
        } else {
          setTimeout(start, 1000);
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.loadController?.abort();
      }

      /**
       * Reload recommendations for the variant now selected on the PDP. Driven by
       * the variant switcher's enroute:variant-changed event; also callable directly.
       * When the variant maps to a related product we upsell for that product
       * instead of the page's main one.
       *
       * @param {object} variant - a variant entry from product-info-json.
       */
      update(variant) {
        const targetId = this.resolveRecommendationId(variant);
        if (targetId === this.recommendedForId) return;
        this.recommendedForId = targetId;
        this.loadRecommendations(targetId);
      }

      /**
       * Resolve which product's recommendations to show for a variant: the
       * related product's numeric id when the variant has one, else the main
       * product. Recommendations need a numeric product id (a handle won't do).
       * @param {object} variant
       * @returns {string} product id
       */
      resolveRecommendationId(variant) {
        const relatedKey = variant?.related_product_key;
        if (relatedKey) {
          const related = this.productInfo()?.related_products?.[relatedKey];
          if (related?.id) return String(related.id);
        }
        return this.productId;
      }

      /** Lazily read (and cache) the PDP's product-info JSON. */
      productInfo() {
        if (this.productInfoCache === undefined) {
          const scope = this.closest('section.product.wp.new-look') || document;
          this.productInfoCache = window._erj_?.getProductInfo?.(scope) || null;
        }
        return this.productInfoCache;
      }

      /**
       * Fetch complementary recommendations, falling back to related ones when
       * the store has no complementary picks for this product.
       * @param {string} [productId] - product to recommend for; defaults to the main product.
       */
      async loadRecommendations(productId = this.productId) {
        if (!productId) return;

        // Cancel any recommendation fetch still in flight from a previous
        // variant so a slow earlier response can't clobber the current one.
        this.loadController?.abort();
        this.loadController = new AbortController();
        const signal = this.loadController.signal;
        if (this.loadingMarkup) {
          this.innerHTML = this.loadingMarkup;
          this.setAttribute('aria-busy', 'true');
        }

        const base =
          window.Shopify.routes.root +
          'recommendations/products?product_id=' +
          productId +
          '&limit=10&section_id=product-frequently-together&intent=';
        try {
          const html =
            (await this.fetchSection(base + 'complementary', signal)) ||
            (await this.fetchSection(base + 'related', signal));
          if (html) this.render(html);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('[product-frequently-together] load failed', error);
          }
        } finally {
          // A superseded request must not clear the current request's skeleton.
          if (!signal.aborted && this.loadingMarkup) {
            if (this.querySelector('.pd2610-look-skeleton')) this.replaceChildren();
            this.removeAttribute('aria-busy');
          }
        }
      }

      /**
       * @param {string} url - Recommendations section URL.
       * @param {AbortSignal} signal - Signal for this fetch.
       * @returns {Promise<string|null>} Inner HTML of `.container`, or null when
       *   the response has no (non-empty) container.
       */
      async fetchSection(url, signal) {
        const response = await fetch(url, { signal });
        const text = await response.text();
        const wrapper = document.createElement('div');
        wrapper.innerHTML = text;
        const container = wrapper.querySelector('.container');
        return container && container.innerHTML.trim().length
          ? container.innerHTML
          : null;
      }

      /**
       * Inject the recommendations markup and bind interactions.
       * @param {string} html
       */
      render(html) {
        if (this.closest('.product-details-new-2610')) {
          const template = document.createElement('template');
          template.innerHTML = html;
          template.content.querySelectorAll('.btn').forEach((button) => {
            button.classList.replace('btn', 'pd2610-btn');
          });
          html = template.innerHTML;
        }
        this.innerHTML = html;
        if (this.closest('.product-details-new-2610')) this.layoutLookCards();
        const signal = this.abortController.signal;

        const cta = this.querySelector('.frequently-cta-button');
        if (cta) {
          cta.addEventListener('click', () => this.addSelectedToCart(), { signal });
        }

        for (const item of this.querySelectorAll('.hvg-frequently-item')) {
          const checkbox = item.querySelector("input[type='checkbox']");
          if (checkbox) {
            checkbox.addEventListener('click', (event) => this.onSelectionChanged(event), {
              signal,
            });
          }
          const select = item.querySelector('select');
          if (select) {
            select.addEventListener('change', (event) => this.onVariantChanged(event), {
              signal,
            });
          }
        }
      }

      /** Reuse the fetched item controls in the new PDP's vertical look cards. */
      layoutLookCards() {
        const grid = this.querySelector('.hvg-frequently-grid');
        if (!grid) return;
        this.classList.add('pd2610-look');
        for (const item of this.querySelectorAll('.hvg-frequently-item')) {
          const checkbox = item.querySelector("input[type='checkbox']");
          if (!checkbox) continue;
          const thumb = grid.querySelector('[id="product_' + checkbox.dataset.pid + '"]');
          if (!thumb) continue;
          // The main item's thumbnail has no link in the legacy response.
          if (!thumb.querySelector('a')) {
            const link = document.createElement('a');
            link.href = checkbox.dataset.url;
            link.setAttribute('aria-label', checkbox.dataset.title);
            thumb.appendChild(link);
          } else {
            thumb.querySelector('a').setAttribute('aria-label', checkbox.dataset.title);
          }
          checkbox.setAttribute('aria-label', checkbox.dataset.title);
          item.prepend(thumb, checkbox);
          const select = item.querySelector('select');
          if (select) {
            select.setAttribute('aria-label', checkbox.dataset.title);
            this.createLookPicker(select, checkbox);
          } else {
            const variant = document.createElement('span');
            variant.className = 'pd2610-look-variant';
            variant.textContent = checkbox.dataset.variantText === 'Default Title'
              ? '' : checkbox.dataset.variantText;
            item.querySelector('.hvg-frequently-item__right').prepend(variant);
          }
          const info = document.createElement('div');
          info.className = 'pd2610-look-info';
          info.append(
            item.querySelector('.hvg-frequently-item__left'),
            item.querySelector('.hvg-frequently-item__right')
          );
          item.appendChild(info);
        }
        grid.hidden = true;
        const cta = this.querySelector('.frequently-cta-button');
        if (cta) {
          // A native button supports keyboard activation as well as clicks.
          const button = document.createElement('button');
          button.type = 'button';
          button.className = cta.className;
          button.textContent = cta.textContent;
          cta.replaceWith(button);
        }
      }

      closeLookPickers() {
        for (const popup of this.querySelectorAll('.pd2610-look-popup')) {
          popup.hidden = true;
          popup.anchorElement?.setAttribute('aria-expanded', 'false');
        }
      }

      /** Keep the native select as the source of truth for prices and cart data. */
      createLookPicker(select, checkbox) {
        const signal = this.abortController.signal;
        const wrapper = document.createElement('span');
        wrapper.className = 'pd2610-look-select';
        select.replaceWith(wrapper);
        wrapper.appendChild(select);
        select.hidden = true;
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'pd2610-look-select-trigger';
        trigger.setAttribute('aria-label', checkbox.dataset.title + ': ' + select.selectedOptions[0].textContent.trim());
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.textContent = select.selectedOptions[0].textContent.trim();
        wrapper.appendChild(trigger);

        const popup = document.createElement('anchored-popup');
        popup.className = 'hvg-cart-variant-switcher-popup pd2610-look-popup';
        popup.id = 'pd2610-look-variants-' + this.productId + '-' + checkbox.dataset.pid;
        popup.hidden = true;
        popup.setAttribute('min-width', '180');
        popup.setAttribute('max-height', '280');
        popup.setAttribute('fixed', '');
        popup.setAttribute('role', 'listbox');
        popup.setAttribute('aria-label', checkbox.dataset.title);
        popup.anchorElement = trigger;
        trigger.setAttribute('aria-controls', popup.id);
        const group = document.createElement('div');
        group.className = 'hvg-cart-variant-switcher-popup-optionblock';
        popup.appendChild(group);
        const sync = () => {
          trigger.textContent = select.selectedOptions[0].textContent.trim();
          trigger.setAttribute('aria-label', checkbox.dataset.title + ': ' + trigger.textContent);
          for (const button of group.children) {
            const selected = button.dataset.value === select.value;
            button.classList.toggle('is-selected', selected);
            button.setAttribute('aria-selected', String(selected));
            button.lastElementChild.hidden = !selected;
            button.tabIndex = selected && !button.disabled ? 0 : -1;
          }
        };
        for (const option of select.options) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'hvg-cart-variant-switcher-popup-optionblock-item';
          button.setAttribute('role', 'option');
          button.dataset.value = option.value;
          button.disabled = option.disabled;
          if (option.disabled) button.setAttribute('data-disabled', '');
          const name = document.createElement('span');
          name.className = 'hvg-cart-variant-switcher-popup-optionblock-item-name' + (option.disabled ? '-disabled' : '');
          name.textContent = option.textContent.trim();
          const check = document.createElement('span');
          check.className = 'hvg-cart-variant-switcher-popup-optionblock-item-check';
          check.setAttribute('aria-hidden', 'true');
          button.append(name, check);
          button.addEventListener('click', () => {
            if (button.disabled) return;
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            this.closeLookPickers();
            trigger.focus();
          }, { signal });
          group.appendChild(button);
        }
        select.addEventListener('change', sync, { signal });
        const open = () => {
          this.closeLookPickers();
          popup.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          (group.querySelector('[aria-selected="true"]:not(:disabled)') || group.querySelector('button:not(:disabled)'))?.focus();
        };
        trigger.addEventListener('click', () => {
          if (popup.hidden) open();
          else this.closeLookPickers();
        }, { signal });
        const navigate = (event) => {
          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (popup.hidden) { open(); return; }
          const buttons = [...group.querySelectorAll('button:not(:disabled)')];
          const index = buttons.indexOf(document.activeElement);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
            : (index + (event.key === 'ArrowUp' ? -1 : 1) + buttons.length) % buttons.length;
          buttons[next]?.focus();
        };
        trigger.addEventListener('keydown', navigate, { signal });
        popup.addEventListener('keydown', navigate, { signal });
        // Keep the floating menu outside the horizontally scrolling card grid.
        this.appendChild(popup);
        sync();
      }

      /** Checkbox toggled: show/hide the matching thumbnail and recompute totals. */
      onSelectionChanged(event) {
        this.toggleProductSelection(event.target.dataset.pid);
      }

      /** @param {string} pid */
      toggleProductSelection(pid) {
        if (this.closest('.product-details-new-2610')) {
          this.setTotal();
          return;
        }
        const thumb = this.querySelector('#product_' + pid);
        if (!thumb) return;
        thumb.classList.toggle('hide');
        this.setPlusPlaceholders();
        this.setTotal();
      }

      /** Show a "+" separator only between two visible thumbnails. */
      setPlusPlaceholders() {
        const grid = this.querySelector('.hvg-frequently-grid');
        if (!grid) return;
        const nodes = grid.querySelectorAll('div');
        let shouldBeVisible = false;
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i].classList.contains('hvg-plus-separator')) {
            nodes[i].classList.toggle('hide', !shouldBeVisible);
            shouldBeVisible = false;
          } else if (
            nodes[i].classList.contains('hvg-frequently-thumb') &&
            !nodes[i].classList.contains('hide')
          ) {
            for (let j = i + 1; j < nodes.length; j++) {
              if (
                nodes[j].classList.contains('hvg-frequently-thumb') &&
                !nodes[j].classList.contains('hide')
              ) {
                shouldBeVisible = true;
                break;
              }
            }
          }
        }
      }

      /** Recompute the "Total" line from all checked items. */
      setTotal() {
        const total = this.querySelector('.frequently-cta-total');
        const itemcontainer = this.querySelector('.hvg-frequently-bottom');
        if (!total || !itemcontainer) return;

        let price = 0;
        let cap = 0;
        for (const item of itemcontainer.querySelectorAll('input:checked')) {
          cap += item.dataset.cap === '' ? parseInt(item.dataset.price) : parseInt(item.dataset.cap);
          price += parseInt(item.dataset.price);
        }

        const text = total.childNodes[0];
        const wrapper = document.createElement('div');
        wrapper.className = 'hvg-frequently-item-price';
        wrapper.appendChild(this.moneyEl(price));

        if (cap > price) {
          const struck = document.createElement('s');
          struck.appendChild(this.moneyEl(cap));
          wrapper.appendChild(struck);
        }

        total.innerHTML = '';
        total.appendChild(text);
        total.appendChild(wrapper);
      }

      /**
       * Build a `<span class="money">` for a cents amount, matching the markup
       * the Liquid template renders for prices. Shopify.formatMoney's output
       * already carries its own `.money` span when the shop's money format is
       * configured that way, so reuse it instead of double-wrapping.
       * @param {number} cents
       * @returns {HTMLElement}
       */
      moneyEl(cents) {
        const formatted = Shopify.formatMoney(cents);
        if (formatted.includes('class="money"')) {
          const wrapper = document.createElement('span');
          wrapper.innerHTML = formatted;
          return wrapper.firstElementChild;
        }
        const el = document.createElement('span');
        el.className = 'money';
        el.innerHTML = formatted;
        return el;
      }

      /** Variant `<select>` changed: sync checkbox data, price, thumbnail, total. */
      onVariantChanged(event) {
        const select = event.target;
        for (const option of select.querySelectorAll('option')) {
          if (select.value !== option.value) continue;

          const checkbox = this.querySelector("input[data-pid='" + option.dataset.pid + "']");
          if (checkbox) {
            checkbox.setAttribute('data-vid', option.value);
            checkbox.setAttribute('data-price', option.dataset.variantprice);
            checkbox.setAttribute('data-cap', option.dataset.variantcap);
            checkbox.setAttribute('data-image', option.dataset.variantimg || '');
            checkbox.setAttribute('data-variant-text', option.dataset.variantTitle || '');
          }

          const price = this.querySelector('#price_' + option.dataset.pid);
          if (price) {
            const cap = document.createElement('s');
            cap.innerHTML = Shopify.formatMoney(option.dataset.variantcap);
            price.innerHTML = Shopify.formatMoney(option.dataset.variantprice) + ' ';
            if (option.dataset.variantcap > option.dataset.variantprice) price.appendChild(cap);
          }

          const thumb = this.querySelector('#product_' + option.dataset.pid);
          if (thumb) {
            thumb.style.backgroundImage = "url('" + option.dataset.variantimg + "')";
          }

          this.setTotal();
        }
      }

      /**
       * Same contract as <product-add-to-cart> (assets/product-add-to-cart.js
       * _applyLetterCustomizerProperties): "this item" in the list above is always the
       * main product, so if it carries a <letter-customizer>, its typed text/style ride
       * along as line item properties on that item — and an invalid input aborts the add.
       * @param {Array<object>} items - items about to be sent to the cart.
       * @returns {boolean} false when the customizer exists but is invalid (surfaces the
       *   field error and aborts the add).
       */
      applyLetterCustomizerProperties(items) {
        const productDetails = this.closest('.product-details.new-look');
        const customizer = (productDetails || document).querySelector('letter-customizer');
        if (!customizer || typeof customizer.getCartProperties !== 'function') return true;

        const { valid, properties } = customizer.getCartProperties();
        if (!valid) {
          customizer.reportInvalid?.();
          return false;
        }

        const target = items.find((item) => item.productId === Number(this.productId));
        if (target && properties) {
          target.properties = Object.assign({}, target.properties, properties);
        }
        return true;
      }

      /** Collect checked variants and delegate the cart mutation to Vue. */
      addSelectedToCart() {
        try {
          gtag('event', 'frequently_cta_button_click', {});
        } catch (error) {
          console.error('track frequently-cta-button error', error);
        }

        const items = [];
        const itemcontainer = this.querySelector('.hvg-frequently-bottom');
        if (itemcontainer) {
          for (const input of itemcontainer.querySelectorAll('input:checked')) {
            items.push({
              id: input.dataset.vid,
              quantity: 1,
              productId: Number(input.dataset.pid) || null,
              handle: input.dataset.handle || '',
              title: input.dataset.title || '',
              image: input.dataset.image || '',
              variantText: input.dataset.variantText || '',
              url: input.dataset.url || '',
            });
          }
        }

        if (!this.applyLetterCustomizerProperties(items)) return;

        if (typeof window.enrouteCart?.addToCart === 'function') {
          window.enrouteCart?.addToCart?.({ items });
        } else {
          console.error('[product-frequently-together] Cart controller is not ready');
        }
      }
    }
  );
}
